import React, { useMemo, useState } from 'react';
import './verticalToolbar.css';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  FaChevronLeft,
  FaChevronRight,
  FaChevronUp,
  FaChevronDown,
  FaCheckCircle,
  FaTimesCircle,
  FaChartBar,
  FaTable,
} from 'react-icons/fa';

import * as techActions from '../../app/actions/technologyActions';
import * as altActions from '../../app/actions/alternativeActions';
import * as expActions from '../../app/actions/experienceActions';
import * as innActions from '../../app/actions/innovationActions';
import * as assActions from '../../app/actions/assistanceActions';
import * as finActions from '../../app/actions/financialActions';
import * as visActions from '../../app/actions/visionActions';
import * as regActions from '../../app/actions/regulationActions';
import {
  canShowIntegrationDashboardBridge,
  canShowIntegrationViewBridge,
  listIntegrationTargetsFromSource,
} from '../../app/helpers/semanticSearchConfigHelper';
import { MODULE_INTEGRATION_META } from './moduleIntegrationMeta';

/**
 * @param {object} props
 * @param {'search'|'view'} [props.variant] search = select/compare + integration; view = solo integration (1 registro)
 * @param {function} [props.onCompareClick]
 * @param {function} [props.onIntegrationClick] (targetModuleKey) => void
 */
const VerticalToolbar = ({
  hasRecords,
  onCompareClick,
  onIntegrationClick,
  moduleSelected,
  variant = 'search',
}) => {
  const [viewMode, setViewMode] = useState('icons');
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const configuration = useSelector((state) => state.configuration);

  const tech = useSelector((state) => state.technology);
  const alt = useSelector((state) => state.alternative);
  const exp = useSelector((state) => state.experience);
  const inn = useSelector((state) => state.innovation);
  const ass = useSelector((state) => state.assistance);
  const fin = useSelector((state) => state.financial);
  const vis = useSelector((state) => state.vision);
  const reg = useSelector((state) => state.regulation);

  const moduleKey = moduleSelected?.key;

  const moduleActionsMap = {
    technology: {
      selectAll: techActions.technologyListSelectAllLimit,
      cleanAll: techActions.technologyCleanAllSelected,
      moreThanOne: tech.moreThanOneIsSelected,
      totalSelected: tech.numberTechnologySelected,
    },
    alternative: {
      selectAll: altActions.alternativeListSelectAllLimit,
      cleanAll: altActions.alternativeCleanAllSelected,
      moreThanOne: alt.moreThanOneIsSelected,
      totalSelected: alt.numberAlternativeSelected,
    },
    experience: {
      selectAll: expActions.experienceListSelectAllLimit,
      cleanAll: expActions.experienceCleanAllSelected,
      moreThanOne: exp.moreThanOneIsSelected,
      totalSelected: exp.numberExperienceSelected,
    },
    innovation: {
      selectAll: innActions.innovationListSelectAllLimit,
      cleanAll: innActions.innovationCleanAllSelected,
      moreThanOne: inn.moreThanOneIsSelected,
      totalSelected: inn.numberInnovationSelected,
    },
    assistance: {
      selectAll: assActions.assistanceListSelectAllLimit,
      cleanAll: assActions.assistanceCleanAllSelected,
      moreThanOne: ass.moreThanOneIsSelected,
      totalSelected: ass.numberAssistanceSelected,
    },
    financial: {
      selectAll: finActions.financialListSelectAllLimit,
      cleanAll: finActions.financialCleanAllSelected,
      moreThanOne: fin.moreThanOneIsSelected,
      totalSelected: fin.numberFinancialSelected,
    },
    vision: {
      selectAll: visActions.visionListSelectAllLimit,
      cleanAll: visActions.visionCleanAllSelected,
      moreThanOne: vis.moreThanOneIsSelected,
      totalSelected: vis.numberVisionSelected,
    },
    regulation: {
      selectAll: regActions.regulationListSelectAllLimit,
      cleanAll: regActions.regulationCleanAllSelected,
      moreThanOne: reg.moreThanOneIsSelected,
      totalSelected: reg.numberRegulationSelected,
    },
  };

  const currentConfig = moduleActionsMap[moduleKey] || {};
  const totalSelected = Number(currentConfig.totalSelected || 0);

  const showIntegration =
    variant === 'view'
      ? canShowIntegrationViewBridge(configuration, moduleKey)
      : canShowIntegrationDashboardBridge(configuration, moduleKey);

  const integrationTargets = useMemo(() => {
    if (!showIntegration || !moduleKey) return [];
    return listIntegrationTargetsFromSource(configuration, moduleKey);
  }, [showIntegration, configuration, moduleKey]);

  if (!hasRecords && variant === 'search') return null;
  if (variant === 'view' && !showIntegration) return null;

  const handleSelectAll = () => {
    if (currentConfig?.selectAll) dispatch(currentConfig.selectAll());
  };

  const handleDeselectAll = () => {
    if (currentConfig?.cleanAll) dispatch(currentConfig.cleanAll());
  };

  const getTooltip = (text) => (viewMode !== 'expanded' ? text : '');

  const integrationEnabled = variant === 'view' ? true : totalSelected >= 1;

  return (
    <div className={`vertical-toolbar-container mode-${viewMode}`}>
      {viewMode !== 'compact' && (
        <div className="toolbar-actions">
          {variant === 'search' && (
            <>
              <button
                type="button"
                onClick={handleSelectAll}
                className="action-btn"
                title={getTooltip(t('semanticSearch.integration.selectFirst9'))}
              >
                <FaCheckCircle className="react-icon green" />
                {viewMode === 'expanded' && (
                  <span className="label">{t('semanticSearch.integration.selectFirst9')}</span>
                )}
              </button>

              <button
                type="button"
                onClick={handleDeselectAll}
                className="action-btn"
                title={getTooltip(t('semanticSearch.integration.deselectAll'))}
              >
                <FaTimesCircle className="react-icon red" />
                {viewMode === 'expanded' && (
                  <span className="label">{t('semanticSearch.integration.deselectAll')}</span>
                )}
              </button>

              <button
                type="button"
                onClick={() => onCompareClick && onCompareClick('field')}
                className={`action-btn ${!currentConfig.moreThanOne ? 'disabled-style' : ''}`}
                title={getTooltip(t('semanticSearch.integration.compareField'))}
                disabled={!currentConfig.moreThanOne}
              >
                <div className="badge-wrapper">
                  <FaChartBar
                    className={`react-icon ${currentConfig.moreThanOne ? 'blue' : 'gray'}`}
                  />
                  {totalSelected > 0 && viewMode !== 'expanded' && (
                    <span className="badge-notification">{totalSelected}</span>
                  )}
                </div>
                {viewMode === 'expanded' && (
                  <span className="label">
                    {t('semanticSearch.integration.compareField')}
                    {totalSelected > 0 ? ` (${totalSelected})` : ''}
                  </span>
                )}
              </button>

              <button
                type="button"
                onClick={() => onCompareClick && onCompareClick('matrix')}
                className={`action-btn ${!currentConfig.moreThanOne ? 'disabled-style' : ''}`}
                title={getTooltip(t('semanticSearch.integration.compareMatrix'))}
                disabled={!currentConfig.moreThanOne}
              >
                <div className="badge-wrapper">
                  <FaTable
                    className={`react-icon ${currentConfig.moreThanOne ? 'blue' : 'gray'}`}
                  />
                  {totalSelected > 0 && viewMode !== 'expanded' && (
                    <span className="badge-notification">{totalSelected}</span>
                  )}
                </div>
                {viewMode === 'expanded' && (
                  <span className="label">
                    {t('semanticSearch.integration.compareMatrix')}
                    {totalSelected > 0 ? ` (${totalSelected})` : ''}
                  </span>
                )}
              </button>
            </>
          )}

          {showIntegration &&
            integrationTargets.map((targetKey) => {
              const meta = MODULE_INTEGRATION_META[targetKey];
              if (!meta) return null;
              const Icon = meta.Icon;
              const label = t(meta.labelKey);
              return (
                <button
                  key={targetKey}
                  type="button"
                  onClick={() => onIntegrationClick && onIntegrationClick(targetKey)}
                  className={`action-btn ${!integrationEnabled ? 'disabled-style' : ''}`}
                  title={getTooltip(label)}
                  disabled={!integrationEnabled}
                >
                  <div className="badge-wrapper">
                    <Icon className={`react-icon ${integrationEnabled ? 'teal' : 'gray'}`} />
                    {variant === 'search' && totalSelected > 0 && viewMode !== 'expanded' && (
                      <span className="badge-notification">{totalSelected}</span>
                    )}
                  </div>
                  {viewMode === 'expanded' && (
                    <span className="label">
                      {label}
                      {variant === 'search' && totalSelected > 0 ? ` (${totalSelected})` : ''}
                    </span>
                  )}
                </button>
              );
            })}
        </div>
      )}

      <div className="control-group">
        {viewMode !== 'expanded' && (
          <button
            type="button"
            className="ctrl-btn expand"
            onClick={() => setViewMode(viewMode === 'compact' ? 'icons' : 'expanded')}
          >
            <FaChevronLeft />
          </button>
        )}

        <button
          type="button"
          className="ctrl-btn toggle"
          onClick={() => setViewMode(viewMode === 'compact' ? 'icons' : 'compact')}
        >
          {viewMode === 'compact' ? <FaChevronUp /> : <FaChevronDown />}
        </button>

        {viewMode !== 'compact' && (
          <button
            type="button"
            className="ctrl-btn shrink"
            onClick={() => setViewMode(viewMode === 'expanded' ? 'icons' : 'compact')}
          >
            <FaChevronRight />
          </button>
        )}
      </div>
    </div>
  );
};

export default VerticalToolbar;
